import { computed, ComputedRef, Ref } from '@vue/composition-api';
import { addDays } from 'date-fns';
import { EventV2 } from '@front-app/services/EventsApi/EventModel';
import { isActiveEvent, isComingSoonEvent, orderEventsOldToNew } from '@front-app/views/home/events/filters';

export enum EventBucketType {
	ACTIVE = 'ACTIVE',
	COMING_SOON = 'COMING_SOON',
	MONTHLY = 'MONTHLY',
}

export interface EventBucket {
	type: EventBucketType;
	// The day of the event's start OR if isActiveEventsBucket = true, this is today
	date: Date;
	// The events in the bucket
	events: EventV2[];
}

const getBucketKey = (startDate: Date, type: EventBucketType): Date => {
	const date = new Date(startDate);
	date.setMilliseconds(0);
	date.setSeconds(0);
	date.setMinutes(0);
	date.setHours(0);
	if (type === EventBucketType.MONTHLY) {
		date.setDate(1);
	}
	return date;
};

const getEmptyBucket = (key: Date, bucketType: EventBucketType): EventBucket => ({
	type: bucketType,
	events: [],
	date: key,
});

const getEventBucketType = (event: EventV2): EventBucketType => {
	if (isActiveEvent(event)) {
		return EventBucketType.ACTIVE;
	}
	if (isComingSoonEvent(event)) {
		return EventBucketType.COMING_SOON;
	}
	return EventBucketType.MONTHLY;
};

// Normalize the start date for events in order to bucket them together
const normalizeEventStartDate = (type: EventBucketType, startDate: Date): Date => {
	switch (type) {
	case EventBucketType.ACTIVE:
		return new Date();
	case EventBucketType.MONTHLY:
		return startDate;
	case EventBucketType.COMING_SOON:
		// Group events that are coming soon in the next day bucket for ease
		return addDays(new Date(), 1);
	default:
		// eslint-disable-next-line no-case-declarations
		const exhaustiveCheck: never = type;
		throw new Error(`Unhandled event bucket type case: ${exhaustiveCheck}`);
	}
};

// Bucket together events happening in the same month, current events or those coming soon
function bucketEvents(events: EventV2[]): EventBucket[] {
	const buckets = events
		.sort(orderEventsOldToNew)
		.reduce((accumulated: Record<string, EventBucket>, event: EventV2) => {
			const bucketType = getEventBucketType(event);
			const startDate = normalizeEventStartDate(bucketType, event.startDate);
			const key = getBucketKey(startDate, bucketType);

			const bucket = accumulated[key.toISOString()] || getEmptyBucket(key, bucketType);
			bucket.events.push(event);
			// eslint-disable-next-line no-param-reassign
			accumulated[key.toISOString()] = bucket;

			return accumulated;
		}, {});

	const sortedBuckets = Object.values(buckets).sort((a, b) => a.date.getTime() - b.date.getTime());

	return sortedBuckets;
}

export interface UseEventBuckets {
	hasActiveEvents: ComputedRef<boolean>;
	hasComingSoonEvents: ComputedRef<boolean>;
	bucketedEvents: ComputedRef<EventBucket[]>;
}

/**
 * Groups events together by month. Events which are happening any time today
 * are grouped into their own bucket with `isActiveEventsBucket = true`
 *
 * @param events A list of events to bucket
 * @returns
 */
export const useEventBuckets = (events: Ref<EventV2[]>): UseEventBuckets => {
	const bucketedEvents = computed(() => bucketEvents(events.value));
	const hasActiveEvents = computed(() => bucketedEvents.value.some(({ type }) => type === EventBucketType.ACTIVE));
	const hasComingSoonEvents = computed(() => bucketedEvents.value.some(({ type }) => type === EventBucketType.COMING_SOON));
	return {
		hasActiveEvents,
		hasComingSoonEvents,
		bucketedEvents,
	};
};
